/**
 * The editor — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * The map bar, the tool rail, the canvas with its floating controls, and the
 * Layers panel. What ties them together is small and worth naming:
 *
 * **One active layer, and drawing always goes on it.** The chip bottom-left
 * says which, and picking another is how you move the pen — not a property of
 * the tool. A layer somebody cannot change turns the chip amber and disables
 * drawing with the tooltip §5.3 names, rather than letting a shape be drawn
 * and then refused.
 *
 * **Nothing waits for a Save.** `useMapEditing` sends every finished shape,
 * move, rename and delete as it happens and the status pill says so; the only
 * button on this screen that commits anything is in a dialog.
 *
 * **The imagery is a separate failure from the map.** §5.7's panel replaces
 * the canvas and says the shapes are safe, because they are: they live on the
 * property, not in the photo.
 *
 * The right-hand panel is the Layers panel alone for now. §5.3's tab row —
 * **Layers · N · Measurements · N · History** — arrives with the Measurements
 * panel (§5.4) and the map's own history route, because a tab row whose other
 * two tabs open onto nothing is worse than one panel that does not pretend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import {
  LAYER_PALETTE, formatArea, formatLength, geometryProblem, measuresOf, unitFamily,
} from '@rowted/shared'
import type { Geometry, LayerColor, LngLat } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { RAIL_WIDTH, useCoarse, useNarrow, usePhone } from '../../lib/use-narrow.js'
import { Button, Segmented, Switch } from '../../components/primitives.js'
import {
  IconChevronDown, IconClose, IconLayers, IconNavigate, IconPlus, IconRuler, IconWarn,
} from '../../frame/icons.js'
import type { ImagerySession, MapBaseKind } from '../../lib/map-provider.js'
import { forgetImagery } from '../../lib/map-provider.js'
import {
  deletePart, keepAsMap, measurementsOf, patchMap, patchPart, refreshPart,
  type MapFeature, type MapLayer, type MeasurementGroup, type MeasurementType,
  type OneMapPayload,
} from '../api.js'
import { HistoryTab } from '../../components/history-tab.js'
import type { PanelRow } from '../measure/rows.js'
import { useMeasure } from '../measure/use-measure.js'
import { MeasurementsPanel } from '../measure/panel.js'
import { measureWords } from '../measure/rows.js'
import { MeasurePopup } from '../measure/popup.js'
import { SaveMeasurementsDialog, type SaveRow } from '../measure/save-dialog.js'
import { KeepAsMapDialog } from '../measure/keep-as-map.js'
import { LeavingDialog } from '../measure/leaving.js'
import { CrosshairMark, DrawingBar, ToolDock } from '../phone/tool-dock.js'
import { MoreButton, PhoneEditorShell, PhoneMoreSheet } from '../phone/editor-shell.js'
import { PanelSheet, SheetButton } from '../../components/panel-sheet.js'
import { useMapEditing } from '../use-map-editing.js'
import { TOOL_KEYS, type Draft, type ToolName } from '../draw/index.js'
import { MapCanvas } from './map-canvas.js'
import { useRecordChrome } from '../../record/record-chrome.js'
import { CARD_GUTTER, useFillHeight } from './fill-height.js'
import { MapBar, SharingButton } from './map-bar.js'
import { DeleteLayer, LayersPanel } from './layers-panel.js'
import { LayerOptionsSheet } from '../phone/layer-options.js'
import { ShapePopup } from './shape-popup.js'
import { StatusPill, isLoud, type SaveState } from './status-pill.js'
import { ScaleAndCredit, ZoomStack } from './map-chrome.js'
import { ToolRail } from './tool-rail.js'
import { useDrawing } from './use-drawing.js'
import { useReshape } from './use-reshape.js'
import { MapPanel, PanelActions } from '../states/map-panel.js'
import { NewLayerDialog } from '../dialogs/new-layer.js'
import { LayerSharingDialog } from '../sharing/layer-sharing.js'
import { MapSharingDialog } from '../sharing/map-sharing.js'

const BASES: { value: MapBaseKind; label: string }[] = [
  { value: 'satellite', label: 'Satellite' },
  { value: 'map', label: 'Map' },
  { value: 'hybrid', label: 'Hybrid' },
]

/** Which layers this person has hidden on this map, on this device. */
const HIDDEN = (mapId: string) => `rowted.map.hidden.${mapId}`
/** Whether snapping is on, per browser. A preference, not a map's business. */
const SNAP_KEY = 'rowted.map.snap'

/** §5.3's 300px panel docks from here up; below it, §7's slide-over. */
const DOCK_PANEL = 1024

/** The right panel's three tabs (§5.3). */
type PanelTab = 'layers' | 'measurements' | 'history'

export function MapEditor({
  payload, pin, propertyId, propertyName, canDraw, offerMeasure = false,
  measuring = false, openPanel, onSwitch, onBack, onReload, onMeasure, onDoneMeasuring,
}: {
  payload: OneMapPayload
  pin: LngLat
  /** For the measure session and for saving parts — both are the property's. */
  propertyId: string
  propertyName: string
  canDraw: boolean
  offerMeasure?: boolean
  /** §5.4: the rail is the measuring set and the tape replaces the layers. */
  measuring?: boolean
  /** Which panel tab to open on — `?panel=measurements` from §10's link. */
  openPanel?: PanelTab
  onSwitch?: () => void
  onBack?: () => void
  onReload?: () => void
  /** Picking Measure in the rail — the tab owns `?measure=1`. */
  onMeasure?: () => void
  /** **Done**, or leaving the session another way. */
  onDoneMeasuring?: () => void
}) {
  const { map, divisions } = payload
  const reload = useCallback(() => onReload?.(), [onReload])
  const edit = useMapEditing(payload, reload)
  const { layers, features } = edit

  const [base, setBase] = useState<MapBaseKind>(map.base)
  const [tool, setTool] = useState<ToolName>('select')
  const [imageryProblem, setImageryProblem] = useState<string | null>(null)
  /* **Switch to Map view** puts the panel away; the plain base stays. */
  const [dismissed, setDismissed] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => readHidden(map.id))
  const [snapOn, setSnapOn] = useState(() => readSnap())
  const [activeId, setActiveId] = useState<string | null>(layers[0]?.id ?? null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [live, setLive] = useState<MapLibreMap | null>(null)
  const [imagery, setImagery] = useState<ImagerySession | null>(null)
  const [sharingLayer, setSharingLayer] = useState<MapLayer | null>(null)
  const [sharingMap, setSharingMap] = useState(false)
  const [addingLayer, setAddingLayer] = useState(false)
  const [panel, setPanel] = useState<PanelTab>(openPanel ?? 'layers')
  const [saving, setSaving] = useState<SaveRow[] | null>(null)
  const [keeping, setKeeping] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [kept, setKept] = useState(false)
  const [more, setMore] = useState(false)
  const [layersOpen, setLayersOpen] = useState(false)
  /* §6.4's second level, by id rather than by object: a rename writes
     through `reload`, and a held object would keep drawing the old name. */
  const [layerSheet, setLayerSheet] = useState<string | null>(null)
  const [deletingLayer, setDeletingLayer] = useState<MapLayer | null>(null)
  /** §6.4's Measurements sheet — the panel's tab, on a phone. */
  const [measureSheet, setMeasureSheet] = useState(false)
  /* §6.2.3: Rect and Circle drag by default and the switch turns the sight
     on for them. Area and Line are always under it — a traced finger covers
     the line it is tracing, which is the whole reason the sight exists. */
  const [crosshairOn, setCrosshairOn] = useState(false)
  /** §7: the panel slides over below 1024px rather than docking. */
  const [panelOpen, setPanelOpen] = useState(false)
  const [units, setUnits] = useState<'sq ft' | 'sq yd' | 'ac'>('sq ft')
  const canvasBox = useRef<HTMLDivElement>(null)
  /* §7: the canvas takes the rest of the window, not a flat 560px. */
  const canvasRow = useRef<HTMLDivElement>(null)
  const phone = usePhone()
  const narrow = useNarrow(DOCK_PANEL - 1)
  /*
   * The fold band's top edge is `RAIL_WIDTH` **inclusive**, not `- 1`.
   *
   * §7 writes the band as 1024–1179 and §13's test says 1180, and §13 is the
   * one naming a device: an iPad Air in landscape is exactly 1180 CSS px, so
   * a band that stopped one pixel short would leave the commonest iPad with
   * no fold at all. It costs a mouse nothing — `coarse` is what decides, and
   * a 1180px laptop window keeps the desktop layout either way.
   */
  const midBand = useNarrow(RAIL_WIDTH)
  const coarse = useCoarse()
  const chrome = useRecordChrome()
  /*
   * §7's middle band: wide enough for the desktop layout, too narrow to
   * give 300px away. **1024–1179 with a finger** joins it, but only once
   * the chrome is folded — an iPad in landscape has the room for the docked
   * panel right up until somebody starts drawing, and that is the moment
   * the map wants all of it.
   */
  const slideOver = !phone && (narrow || (coarse && midBand && chrome.folded))

  /*
   * §7: a tap on the canvas, or a drawing tool picked, folds the Property
   * header and tab strip away. **A mouse never folds** — a laptop window at
   * 1100px is a laptop, and chrome that vanishes when you click the thing
   * you came to use is chrome nobody asked to lose.
   */
  const canFold = !phone && coarse && midBand && !narrow
  const { fold, unfold } = chrome
  const foldChrome = useCallback(() => { if (canFold) fold() }, [canFold, fold])

  /*
   * Leaving the map puts the chrome back. A folded header is a statement
   * about *this* tab wanting the room, so a tab change or a navigation that
   * left it folded would hand somebody an Overview with no header on it.
   * It also unfolds the moment folding stops being allowed — a rotation
   * into portrait, or an iPad plugged into a mouse.
   */
  useEffect(() => {
    if (!canFold) unfold()
    return unfold
  }, [canFold, unfold])
  /* The phone draws its own full-screen shell, which is already 100dvh. */
  const fillHeight = useFillHeight(canvasRow, !phone, CARD_GUTTER)

  /* The layer whose options level is open, looked up fresh every render. */
  const openLayer = layers.find((one) => one.id === layerSheet) ?? null

  const measure = useMeasure(propertyId, measuring)
  const [groups, setGroups] = useState<MeasurementGroup[]>([])

  /* The saved side of the Measurements panel. Re-read after every save, so
     the group headers stop saying "+ 640 new" the moment it is not new. */
  const readGroups = useCallback(() => {
    measurementsOf(propertyId)
      .then((body) => setGroups(body.groups))
      .catch(() => { /* the panel says "nothing measured yet" */ })
  }, [propertyId])
  useEffect(readGroups, [readGroups])

  /* A different map means a different set of eyes, and a fresh pen. */
  useEffect(() => {
    setHidden(readHidden(map.id))
    setBase(map.base)
    setTool('select')
    setSelectedId(null)
  }, [map.id, map.base])

  /* The active layer must be one that still exists and is still visible. */
  useEffect(() => {
    const ok = layers.find((one) => one.id === activeId && !hidden.has(one.id))
    if (!ok) setActiveId(layers.find((one) => !hidden.has(one.id))?.id ?? null)
  }, [layers, hidden, activeId])

  const active = layers.find((one) => one.id === activeId) ?? null
  const drawing = tool !== 'select' && tool !== 'pan' && tool !== 'measure'

  /**
   * Picking a tool, with the one that is not built yet saying so.
   *
   * §5.4's measure session is the next piece of this build. A rail button
   * that sets a tool which then draws nothing is a control that looks on and
   * is not — so Measure says what it is waiting for and hands the pen back,
   * which is the same answer `?measure=1` gives on the tab.
   */
  const pickTool = useCallback((wanted: ToolName) => {
    if (wanted === 'measure') {
      /* Measure is a *mode*, not a tool that draws: it swaps the rail, the
         panel and where a finished outline goes (§5.4). The tab owns the
         flag because it is in the URL, so this asks rather than sets. */
      if (!measuring) onMeasure?.()
      return
    }
    setTool(wanted)
    /* §7: picking a pen is the moment the map wants the window. */
    if (wanted !== 'select' && wanted !== 'pan') foldChrome()
  }, [measuring, onMeasure, foldChrome])

  /* What snapping may land on: visible layers only (§5.3). */
  const targets = useCallback(() => (snapOn
    ? features
      .filter((one) => !hidden.has(one.layerId))
      .map((one) => ({ geometry: one.geometry as Geometry }))
    : []), [features, hidden, snapOn])

  const finished = useCallback((made: Draft) => {
    /* The one branch measuring adds to the drawing path: a tape shape is
       never sent to the map endpoints (§5.4), so it stops here. */
    if (measuring) { measure.add(made); return }
    if (!active) return
    void edit.drawFeature(active.id, {
      kind: made.kind,
      geometry: made.geometry,
      shape: made.shape,
      name: '',
    }).then((drawn) => {
      /*
       * §6.2: finishing opens the shape's own panel. It is where the name,
       * the colour and *Save to property measurements* are, and a shape that
       * lands unnamed and unselected is one somebody has to hunt for in the
       * layer list to do the next obvious thing to it. Both shells do it —
       * the phone opens a sheet and the desktop a popup, which is the same
       * decision drawn two ways.
       */
      if (drawn) { setTool('select'); setSelectedId(drawn.id) }
    })
  }, [measuring, measure, active, edit])

  /*
   * Which tools draw under the crosshair, and where it is on offer.
   *
   * **A phone** (§6.2): Area and Line always, because a traced finger covers
   * the line it is tracing; Rect and Circle only when somebody asks, since a
   * one-finger drag is the better gesture for two opposite corners.
   *
   * **An iPad** (§7): never by default and always on offer. A pencil traces
   * as precisely as a mouse, so forcing the sight on would take the better
   * gesture away from the device that can use it — but a finger on the same
   * screen has the phone's problem exactly, and the switch is how you say so.
   * The switch therefore covers all four drawing tools here, rather than the
   * two it covers on a phone.
   *
   * **A mouse never uses it**, at any width.
   */
  const sightable = tool === 'area' || tool === 'line' || tool === 'rect' || tool === 'circle'
  const offerSight = !phone && coarse && canDraw && sightable
  const underSight = drawing && (phone
    ? (tool === 'area' || tool === 'line' || (crosshairOn && (tool === 'rect' || tool === 'circle')))
    : (coarse && crosshairOn && sightable))

  const { draft, hint, bar } = useDrawing({
    map: drawing && canDraw ? live : null,
    tool,
    targets,
    pin,
    crosshair: underSight,
    onFinished: finished,
    onProblem: setToast,
  })

  const selected = features.find((one) => one.id === selectedId) ?? null

  /* Select is the other gesture: move the shape, or drag one of its corners. */
  const reshape = useReshape({
    map: live,
    feature: tool === 'select' && canDraw ? selected : null,
    enabled: tool === 'select' && canDraw,
    touch: coarse,
    onCommit: (patch) => {
      if (!selected) return
      const problem = geometryProblem(selected.kind, patch.geometry, pin)
      if (problem) { setToast(problem); return }
      void edit.changeFeature(selected.id, patch as never)
    },
  })

  /* A toast that stays is a toast nobody reads twice. */
  useEffect(() => {
    if (!toast) return undefined
    const timer = window.setTimeout(() => setToast(null), 4_000)
    return () => window.clearTimeout(timer)
  }, [toast])

  /* §5.3's keyboard. Never while somebody is typing in the shape popup. */
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        edit.undo()
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const wanted = TOOL_KEYS[event.key.toLowerCase()]
      if (!wanted) return
      if (!canDraw && wanted !== 'select' && wanted !== 'pan') return
      pickTool(wanted)
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [canDraw, edit, pickTool])

  /* The camera, remembered on the map itself so it opens where it was left. */
  useEffect(() => {
    if (!live || !canDraw) return undefined
    const save = () => {
      const centre = live.getCenter()
      void patchMap(map.id, {
        view: { center: [centre.lng, centre.lat], zoom: live.getZoom() },
        version: map.version,
      }).catch(() => { /* A camera is not worth a toast. */ })
    }
    live.on('moveend', save)
    return () => { live.off('moveend', save) }
  }, [live, canDraw, map.id, map.version])

  const summary = useMemo(() => shareSummary(layers), [layers])
  const selectedLayer = layers.find((one) => one.id === selected?.layerId) ?? null

  function toggle(layerId: string) {
    setHidden((was) => {
      const next = new Set(was)
      if (next.has(layerId)) next.delete(layerId)
      else next.add(layerId)
      try {
        window.localStorage.setItem(HIDDEN(map.id), JSON.stringify([...next]))
      } catch { /* private window: the eyes just do not persist */ }
      return next
    })
  }

  /** How many saved parts there are — §5.6: the tab counts saved, only. */
  const savedParts = groups.reduce((count, group) => count + group.parts.length, 0)

  /** Which layer a shape is on, so a mapped row can say `From map · <layer>`. */
  const layerNames = useMemo(() => {
    const names: Record<string, string> = {}
    for (const feature of features) {
      names[feature.id] = layers.find((one) => one.id === feature.layerId)?.name ?? ''
    }
    return names
  }, [features, layers])

  /** **Done**, with §5.4's guard in front of it. */
  const done = useCallback(() => {
    if (measure.unsaved > 0) { setLeaving(true); return }
    measure.clear()
    onDoneMeasuring?.()
  }, [measure, onDoneMeasuring])

  const renamePart = useCallback((row: PanelRow) => {
    const next = window.prompt('Name this measurement', row.name)
    if (next === null || next.trim() === '') return
    if (row.saved) void patchPart(row.id, { name: next.trim() }).then(readGroups)
    else measure.change(row.id, { name: next.trim() })
  }, [measure, readGroups])

  const removePart = useCallback((row: PanelRow) => {
    if (row.saved) void deletePart(row.id).then(readGroups)
    else measure.discard(row.id)
  }, [measure, readGroups])

  /** A row in the panel points at a shape: show it and open its popup. */
  const pickRow = useCallback((row: PanelRow) => {
    if (row.saved) {
      if (!row.featureId) return
      setTool('select')
      setSelectedId(row.featureId)
      return
    }
    measure.setSelectedId(row.id)
  }, [measure])

  const refreshRow = useCallback((row: PanelRow) => {
    void refreshPart(row.id)
      .then(readGroups)
      .catch((cause: unknown) => setToast(
        cause instanceof Error ? cause.message : 'That could not be updated.'))
  }, [readGroups])

  /*
   * The canvas and everything that floats over it, hoisted out of the
   * return because **both shells render the same one**. A phone has no
   * rail, no bar and no docked panel (§6.3), but the map, its overlays,
   * §5.7's panel and the shape it has selected are the same on both — and
   * writing them twice is how two surfaces meant to say the same thing
   * stop saying it.
   */
  const canvas = (
    /*
     * §7: a tap on the canvas folds the chrome. `onPointerDown` in the
     * capture phase rather than a click handler — the map eats its own
     * pointer events, so a click never reaches this box, and capture sees
     * the tap on the way down whatever MapLibre does with it afterwards.
     * It does nothing at all on a mouse (`canFold`), so a desktop and a
     * narrow laptop window are untouched.
     */
    <div
      ref={canvasBox}
      onPointerDownCapture={foldChrome}
      className="relative min-h-0 min-w-0 flex-1"
    >
      <MapCanvas
        base={base}
        pin={pin}
        view={map.view}
        layers={layers}
        features={features}
        hidden={hidden}
        plain={Boolean(imageryProblem)}
        draft={draftShape(draft, reshape.draft, active?.color, selectedLayer?.color)}
        selectedId={selectedId}
        tape={measuring ? measure.tape : undefined}
        drawing={(drawing && canDraw) || reshape.busy}
        crosshair={underSight}
        onPick={tool === 'select' ? setSelectedId : undefined}
        onImageryProblem={setImageryProblem}
        onImagery={setImagery}
        onReady={setLive}
      />

      {/*
        * Top-right: the camera. Bottom-right: the ruler and the credit.
        *
        * The phone shell draws its own 44px stack (§6.2), so this one is
        * absent there rather than sitting behind it — two Zoom outs three
        * pixels apart is the kind of thing only a screenshot finds.
        */}
      {!phone && (
        <ZoomStack
          map={live}
          touch={coarse}
          onHome={() => live?.easeTo({ center: pin, zoom: 18 })}
        />
      )}
      <ScaleAndCredit
        map={live}
        attribution={imageryProblem ? null : imagery?.attribution ?? null}
        capturedAt={imagery?.capturedAt ?? null}
        onPlain={Boolean(imageryProblem)}
      />

      {/*
        * §5.7 over the map rather than instead of it. The shapes are
        * still there on the plain base — which is the sentence the panel
        * itself makes — and a blank rectangle would read as lost work.
        */}
      {imageryProblem && !dismissed && (
        <div className="absolute inset-0 flex items-center justify-center p-5">
          <MapPanel
            icon={<IconWarn size={20} />}
            title="Aerial imagery isn’t loading"
            body={
              'Your layers and shapes are fine — they’re saved to the property.'
              + ' Drawing needs the photo, so try again, or switch to the plain Map'
              + ' view to read them.'
            }
            actions={(
              <PanelActions>
                <Button onClick={() => setDismissed(true)}>Switch to Map view</Button>
                <Button
                  variant="primary"
                  onClick={() => { forgetImagery(); setImageryProblem(null) }}
                >
                  <IconNavigate size={15} />
                  Try again
                </Button>
              </PanelActions>
            )}
          />
        </div>
      )}

      {/*
        * Top-left: which base — on a desktop. A phone has no room for a
        * three-way segment beside a back button and a map name, so it moves
        * into `⋯` (§6.2) and this is absent rather than drawn underneath the
        * phone's own row, which is where a screenshot found it.
        */}
      {/* …and absent again while the compact bar is up, which is centred on
          the same line and would run into it at 768px (§7). Switching the
          photograph is not something anybody does mid-shape. */}
      {!phone && !(underSight && drawing) && (
        <div className="absolute left-3 top-3 rounded-button bg-surface p-0.5 shadow-popover">
          <Segmented options={BASES} value={base} onChange={setBase} label="Map base" />
        </div>
      )}

      {/* Top-centre: the hint while something is in hand — or, under the
          sight, the compact bar that takes its place and says the same
          thing with the controls beside it (§7). */}
      {hint && !(underSight && !phone) && (
        <span className={cn(
          'absolute left-1/2 top-3 -translate-x-1/2 rounded-chip bg-surface px-2.5',
          'py-1 text-sub font-semibold text-text-muted shadow-popover',
        )}
        >
          {hint}
        </span>
      )}

      {/*
        * Bottom-left: what is being drawn on, how the last write went,
        * and whether snapping is on.
        *
        * Board 2 puts the layer and the save sentence in **one** chip
        * joined by a `·` — they are one thought ("drawing here, saved")
        * and two chips an inch apart read as two unrelated facts. A
        * conflict or a failure is the exception: it is warn- or
        * danger-coloured and carries a Refresh link, so it gets its own
        * chip beside this one, where it is harder to miss rather than
        * easier.
        */}
      {/* The phone draws this row itself, at touch size, and its Snap switch
          lives in the drawing bar where it is needed (§6.2). */}
      {!phone && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <ActiveLayerChip
            layers={layers.filter((one) => !hidden.has(one.id))}
            active={active}
            canDraw={canDraw}
            onPick={setActiveId}
            save={edit.save}
          />
          {isLoud(edit.save) && <StatusPill state={edit.save} />}
          {canDraw && (
            <span className={cn(
              'inline-flex h-[30px] items-center gap-2 rounded-button bg-surface px-2.5',
              'text-sub shadow-popover',
            )}
            >
              <Switch
                checked={snapOn}
                onChange={(on) => { setSnapOn(on); writeSnap(on) }}
                label="Snap to edges"
              />
            </span>
          )}
          {/*
            * §7: the same Crosshair switch a phone carries in its drawing
            * bar, here in the same cluster as Snap — they are the two
            * questions about *how* the next shape gets drawn, and a finger
            * on an iPad wants both in one place. Absent on a mouse, and
            * absent for Select, Pan, Marker and Label, which place one
            * point where you tap and have nothing to sight.
            */}
          {offerSight && (
            <span className={cn(
              'inline-flex h-[30px] items-center gap-2 rounded-button bg-surface px-2.5',
              'text-sub shadow-popover',
            )}
            >
              <Switch checked={crosshairOn} onChange={setCrosshairOn} label="Crosshair" />
            </span>
          )}
        </div>
      )}

      {toast && (
        <span
          role="status"
          className={cn(
            'absolute left-1/2 top-14 -translate-x-1/2 rounded-chip bg-danger-bg px-3',
            'py-1.5 text-cell font-semibold text-danger shadow-popover',
          )}
        >
          {toast}
        </span>
      )}

      {selected && selectedLayer && (
        /*
         * §6.3: a phone has no popover anchored to a shape, because the
         * finger that selected it is on the shape. The same body goes in a
         * bottom sheet there — `sheet` drops the anchored box and nothing
         * else about it changes.
         */
        phone
          ? (
            <PanelSheet
              open
              onClose={() => setSelectedId(null)}
              title={selected.name || 'Shape'}
            >
              <ShapePopup
                feature={selected}
                layerColor={selectedLayer.color}
                canEdit={canDraw}
                offerMeasure={offerMeasure}
                at={{ x: 0, y: 0 }}
                sheet
                onChange={(patch) => void edit.changeFeature(selected.id, patch)}
                onDelete={() => { setSelectedId(null); void edit.dropFeature(selected.id) }}
                onClose={() => setSelectedId(null)}
                onSaveMeasurement={() => setSaving(rowsForShape(
                  selected, selectedLayer.name, measure.types))}
              />
            </PanelSheet>
          )
          : (
            <ShapePopup
              feature={selected}
              layerColor={selectedLayer.color}
              canEdit={canDraw}
              offerMeasure={offerMeasure}
              at={popupAt(live, selected.geometry, canvasBox.current)}
              onChange={(patch) => void edit.changeFeature(selected.id, patch)}
              onDelete={() => { setSelectedId(null); void edit.dropFeature(selected.id) }}
              onClose={() => setSelectedId(null)}
              onSaveMeasurement={() => setSaving(rowsForShape(
                selected, selectedLayer.name, measure.types))}
            />
          )
      )}

      {/*
        * §7: the sight and its bar on an iPad.
        *
        * In crosshair mode the map's pointer listeners are not attached at
        * all — panning *is* the gesture — so the bar is not a convenience,
        * it is the only way to place a point. The phone shell draws its own
        * pair below; these are the same two components, which is the point:
        * a rectangle drawn with a thumb on a phone and one drawn with a
        * thumb on an iPad are the same rectangle.
        */}
      {!phone && underSight && <CrosshairMark />}
      {!phone && underSight && drawing && bar && (
        <DrawingBar
          compact
          readout={readoutOf(draft, tool, bar.points)}
          hint="Pan the map under the crosshair, then tap Add point"
          snap={snapOn}
          onSnap={(on) => { setSnapOn(on); writeSnap(on) }}
          canFinish={bar.complete}
          canUndo={bar.points > 0}
          onAdd={bar.addPoint}
          onFinish={bar.finish}
          onUndo={bar.undoPoint}
          onCancel={() => { bar.cancel(); setTool('select') }}
        />
      )}
    </div>
  )

  /*
   * Every dialog and sheet this editor owns, hoisted for the same reason
   * the canvas is: a phone opens the same ones from different controls.
   * `SheetOrDialog` is what makes each of them a bottom sheet there, so
   * nothing here has to know which shell it is inside.
   */
  const dialogs = (
    <>
      {addingLayer && (
        <NewLayerDialog
          mapName={map.name}
          divisions={divisions}
          taken={layers.map((one) => one.color)}
          onCancel={() => setAddingLayer(false)}
          onCreate={async (body) => {
            const id = await edit.newLayer(body)
            setAddingLayer(false)
            if (id) setActiveId(id)
          }}
        />
      )}

      {sharingLayer && (
        <LayerSharingDialog
          layer={sharingLayer}
          divisions={divisions}
          onCancel={() => setSharingLayer(null)}
          onSave={async (patch) => {
            await edit.changeLayer(sharingLayer.id, patch)
            setSharingLayer(null)
          }}
        />
      )}

      {measuring && measure.selected && (phone ? (
        <PanelSheet
          open
          onClose={() => measure.setSelectedId(null)}
          title="Measuring"
        >
          <MeasurePopup
            shape={measure.selected}
            types={measure.types}
            who={null}
            canEdit={canDraw}
            at={{ x: 0, y: 0 }}
            sheet
            onChange={(patch) => measure.change(measure.selected!.id, patch)}
            onDiscard={() => measure.discard(measure.selected!.id)}
            onAdd={() => setSaving(measure.rowsFor(measure.selected!.id))}
          />
        </PanelSheet>
      ) : (
        <MeasurePopup
          shape={measure.selected}
          types={measure.types}
          who={null}
          canEdit={canDraw}
          at={popupAt(live, measure.selected.geometry, canvasBox.current)}
          onChange={(patch) => measure.change(measure.selected!.id, patch)}
          onDiscard={() => measure.discard(measure.selected!.id)}
          onAdd={() => setSaving(measure.rowsFor(measure.selected!.id))}
        />
      ))}

      {saving && saving.length > 0 && (
        <SaveMeasurementsDialog
          propertyId={propertyId}
          rows={saving}
          who={null}
          onCancel={() => setSaving(null)}
          onSaved={(rows) => {
            measure.markSaved(rows)
            setSaving(null)
            readGroups()
            /* **Keep as a map** is the only thing waiting on a save, so a
               session that was leaving carries on leaving. */
            if (leaving) { setLeaving(false); measure.clear(); onDoneMeasuring?.() }
          }}
        />
      )}

      {keeping && (
        <KeepAsMapDialog
          propertyName={propertyName}
          shapeCount={measure.tape.length}
          busy={kept}
          onCancel={() => setKeeping(false)}
          onKeep={(body) => {
            setKept(true)
            void keepAsMap(propertyId, {
              ...body,
              shapes: measure.tape.map((shape) => ({
                kind: shape.kind,
                geometry: shape.geometry,
                shape: shape.shape,
                name: shape.name,
                typeId: shape.typeId,
                typeName: measure.types
                  .find((one) => one.measurementTypeId === shape.typeId)?.name ?? null,
              })),
            })
              .then(() => { measure.clear(); setKeeping(false); onDoneMeasuring?.() })
              .catch((cause: unknown) => setToast(
                cause instanceof Error ? cause.message : 'That could not be kept.'))
              .finally(() => setKept(false))
          }}
        />
      )}

      {/*
        * §6.4: the Layer options level's **Delete layer…** closes the level
        * and raises the same confirm the desktop's `⋯` raises — one dialog,
        * one wording, one offer to move the shapes rather than lose them.
        * It lives out here with the other dialogs rather than inside the
        * sheet, because the sheet is what it is replacing.
        */}
      {deletingLayer && (
        <DeleteLayer
          layer={deletingLayer}
          shapes={features.filter((one) => one.layerId === deletingLayer.id).length}
          others={layers.filter((one) => one.id !== deletingLayer.id)}
          onCancel={() => setDeletingLayer(null)}
          onConfirm={(moveTo) => {
            void edit.dropLayer(deletingLayer.id, moveTo)
            setDeletingLayer(null)
            setLayersOpen(false)
          }}
        />
      )}

      {leaving && (
        <LeavingDialog
          count={measure.unsaved}
          onStay={() => setLeaving(false)}
          onDiscard={() => { setLeaving(false); measure.clear(); onDoneMeasuring?.() }}
          onSave={() => setSaving(measure.rowsFor())}
        />
      )}

      {sharingMap && (
        <MapSharingDialog
          mapId={map.id}
          mapName={map.name}
          layers={layers}
          features={features}
          divisions={divisions}
          onClose={() => setSharingMap(false)}
          onChanged={reload}
          onOpenLayer={(layer) => { setSharingMap(false); setSharingLayer(layer) }}
        />
      )}
    </>
  )

  /*
   * The phone shell — §6.2. The map gets the whole screen and the chrome
   * floats; `100dvh` rather than `100vh`, because `vh` on iOS is the height
   * the screen would have without the browser's own bars and a dock measured
   * against it sits under the address bar.
   *
   * `overscroll-none` and `touch-none` on the canvas layer stop the page's
   * pull-to-refresh starting inside the map — a drag that was meant to be a
   * rectangle and reloads the editor instead loses the shape.
   */
  if (phone) {
    return (
      <div
        /*
         * `z-[60]`: the record page's phone tab bar is `z-50`, and §6.2 is
         * explicit that the editor sits **above** it — a map with somebody
         * else's navigation across its bottom third is not the whole screen.
         */
        className="fixed inset-0 z-[60] flex flex-col overscroll-none bg-bg"
        style={{ height: '100dvh' }}
      >
        <div ref={canvasBox} className="relative min-h-0 flex-1 touch-none">
          {canvas}

          <PhoneEditorShell
            mapName={map.name}
            measuring={measuring}
            layerCount={layers.length}
            onBack={() => (measuring ? done() : onBack?.())}
            onSwitch={() => { if (!measuring) onSwitch?.() }}
            onLayers={() => setLayersOpen(true)}
            onZoomIn={() => live?.zoomIn()}
            onZoomOut={() => live?.zoomOut()}
            onHome={() => live?.easeTo({ center: pin, zoom: 18 })}
            activeChip={!measuring && canDraw ? (
              <ActiveLayerChip
                layers={layers.filter((one) => !hidden.has(one.id))}
                active={active}
                canDraw={canDraw}
                onPick={setActiveId}
                save={edit.save}
              />
            ) : undefined}
          >
            <MoreButton onClick={() => setMore(true)} />
          </PhoneEditorShell>

          {underSight && <CrosshairMark />}

          {/* The dock is replaced while something is in hand, never covered
              — a tap meant for ✓ must not land on Circle (§6.2). */}
          {drawing && bar ? (
            <DrawingBar
              readout={readoutOf(draft, tool, bar.points)}
              /* §6.2's own words, not the tool's: under the sight the gesture
                 is panning and tapping, which is nothing like clicking
                 corners — and the hint is the only place that is said. */
              hint={underSight
                ? 'Pan the map under the crosshair, then tap Add point'
                : (hint ?? 'Drag on the map to draw')}
              snap={snapOn}
              onSnap={(on) => { setSnapOn(on); writeSnap(on) }}
              {...(tool === 'rect' || tool === 'circle'
                ? { crosshair: crosshairOn, onCrosshair: setCrosshairOn }
                : {})}
              canFinish={bar.complete}
              canUndo={bar.points > 0}
              onAdd={bar.addPoint}
              onFinish={bar.finish}
              onUndo={bar.undoPoint}
              onCancel={() => { bar.cancel(); setTool('select') }}
            />
          ) : (
            <ToolDock
              tool={tool}
              canDraw={canDraw}
              offerMeasure={offerMeasure}
              measuring={measuring}
              onPick={pickTool}
            />
          )}

          {measuring && (
            <div className={cn(
              'pointer-events-auto absolute inset-x-3 bottom-[96px] flex gap-2',
            )}
            >
              {/*
                * §6.4's way into the Measurements sheet while measuring. A
                * desktop reaches it from the panel's tab row; a phone has no
                * panel, so without this a session could measure nine things
                * and offer no way to read them back or save them — the two
                * buttons beside it are "turn this into a map" and "stop".
                */}
              <Button
                className="flex-1"
                onClick={() => setMeasureSheet(true)}
              >
                {measure.tape.length > 0
                  ? `Measurements · ${measure.tape.length}`
                  : 'Measurements'}
              </Button>
              <Button
                className="flex-1"
                disabled={measure.tape.length === 0}
                onClick={() => setKeeping(true)}
              >
                Keep as a map…
              </Button>
              <Button variant="primary" className="flex-1" onClick={done}>Done</Button>
            </div>
          )}
        </div>

        {/*
          * §6.4's Layers sheet — the same panel the desktop docks, and its
          * **second level**. A level replaces the body rather than stacking
          * a second sheet, and the back link names where it goes (`‹ Layers`,
          * not a hard-coded `‹ Filters`), which is the list engine's rule for
          * every sheet in the product.
          */}
        {layersOpen && (
          <PanelSheet
            open
            onClose={() => { setLayersOpen(false); setLayerSheet(null) }}
            title={openLayer ? openLayer.name : 'Layers'}
            sub={openLayer ? undefined : `${map.name} · tap a layer to change it`}
            onBack={openLayer ? () => setLayerSheet(null) : undefined}
            backLabel="Layers"
            footer={openLayer
              ? <SheetButton onClick={() => setLayerSheet(null)}>Done</SheetButton>
              : (canDraw
                ? (
                  <SheetButton
                    onClick={() => { setLayersOpen(false); setAddingLayer(true) }}
                  >
                    New layer
                  </SheetButton>
                )
                : undefined)}
          >
            {openLayer
              ? (
                <LayerOptionsSheet
                  layer={openLayer}
                  shapes={features}
                  others={layers.filter((one) => one.id !== openLayer.id)}
                  divisions={divisions}
                  canEdit={canDraw}
                  onChange={(patch) => void edit.changeLayer(openLayer.id, patch)}
                  onShare={() => {
                    setLayersOpen(false)
                    setLayerSheet(null)
                    setSharingLayer(openLayer)
                  }}
                  onMove={(toLayerId) => {
                    for (const one of features.filter((f) => f.layerId === openLayer.id)) {
                      void edit.changeFeature(one.id, { layerId: toLayerId })
                    }
                  }}
                  onDelete={() => {
                    setLayerSheet(null)
                    setDeletingLayer(openLayer)
                  }}
                  onDuplicated={() => { setLayerSheet(null); reload() }}
                />
              )
              : (
                <LayersPanel
                  phone
                  layers={layers}
                  features={features}
                  divisions={divisions}
                  hidden={hidden}
                  activeId={activeId}
                  selectedId={selectedId}
                  canEdit={canDraw}
                  onToggle={toggle}
                  onActivate={setActiveId}
                  onOpenLayer={(layer) => setLayerSheet(layer.id)}
                  onSelectShape={(id) => {
                    setLayersOpen(false)
                    setTool('select')
                    setSelectedId(id)
                  }}
                  onChangeLayer={(id, patch) => void edit.changeLayer(id, patch)}
                  onDeleteLayer={(id, moveTo) => void edit.dropLayer(id, moveTo)}
                  onDuplicate={reload}
                  onShare={(layer) => { setLayersOpen(false); setSharingLayer(layer) }}
                />
              )}
          </PanelSheet>
        )}

        {more && (
          <PhoneMoreSheet
            base={base}
            onBase={setBase}
            units={units}
            onUnits={setUnits}
            canUndo={edit.canUndo}
            onUndo={edit.undo}
            onMeasurements={offerMeasure ? () => setMeasureSheet(true) : undefined}
            onSharing={canDraw ? () => setSharingMap(true) : undefined}
            onClose={() => setMore(false)}
          />
        )}

        {/*
          * §6.4's Measurements sheet — the desktop panel's second tab, which
          * a phone has no tab row to reach. The body is the **same**
          * `MeasurementsPanel`; what the sheet adds is §6.4's sub-line and
          * its footer, where the count is in the label (`Save 2 to property`)
          * rather than in a sentence beside the button, because a sheet
          * footer is one row and the button is the whole of it.
          */}
        {measureSheet && (
          <PanelSheet
            open
            onClose={() => setMeasureSheet(false)}
            title="Measurements"
            sub={measureWords(savedParts, measure.unsaved)}
            footer={measure.unsaved > 0 && canDraw
              ? (
                <SheetButton
                  onClick={() => { setMeasureSheet(false); setSaving(measure.rowsFor()) }}
                >
                  {`Save ${measure.unsaved} to property`}
                </SheetButton>
              )
              : undefined}
          >
            <MeasurementsPanel
              groups={groups}
              tape={measure.tape}
              types={measure.types}
              layerNames={layerNames}
              canEdit={canDraw}
              onSave={() => { setMeasureSheet(false); setSaving(measure.rowsFor()) }}
              onRename={renamePart}
              onRemove={removePart}
              onRefresh={refreshRow}
              onPick={(row) => { setMeasureSheet(false); pickRow(row) }}
            />
          </PanelSheet>
        )}

        {dialogs}
      </div>
    )
  }

  return (
    /*
     * **The editor is a card, like every other tab body.**
     *
     * It used to be a bare flex column, so the Map tab was the one tab on a
     * record whose content ran edge to edge with no border and no radius
     * while Overview, Jobs, Visits, Files and History were all cards. The
     * canvas is the only thing in the app that fills its box completely, so
     * it was also the one place where the missing frame was obvious.
     *
     * `overflow-hidden` is what makes the radius real — a canvas painted to
     * the box's corners ignores a border radius otherwise — and it is safe
     * here because everything the editor floats (the panel, the dock, the
     * shape popup, the scale bar) is already clamped to the canvas. The
     * phone shell above keeps no border at all: it is `fixed inset-0`, and a
     * rounded corner on a full screen is a gap.
     */
    <div className={cn(
      'flex min-h-0 flex-1 flex-col overflow-hidden rounded-record border border-line',
      'bg-surface',
    )}
    >
      <MapBar
        name={map.name}
        layerCount={layers.length}
        summary={summary}
        onSwitch={onSwitch}
        onBack={onBack}
        actions={(
          <>
            {slideOver && (
              <Button size="toolbar" onClick={() => setPanelOpen((was) => !was)}>
                <IconLayers size={15} />
                {`Layers · ${layers.length}`}
              </Button>
            )}
            <SharingButton onClick={() => setSharingMap(true)} />
          </>
        )}
      />

      {!canDraw && (
        <div className={cn(
          'flex shrink-0 items-center gap-2.5 border-b border-warn/30 bg-warn-bg px-4',
          'py-3 text-cell text-warn',
        )}
        >
          <IconWarn size={14} />
          <span>
            <b>View only.</b>
            {' You can look and print. Drawing needs Edit on Properties.'}
          </span>
        </div>
      )}

      {/*
        * §5.4's strip. It says the one thing somebody measuring needs to
        * know and could not otherwise find out — that none of this is a map
        * and only the numbers are kept — and carries the two ways out.
        */}
      {measuring && (
        <div className={cn(
          'flex shrink-0 flex-wrap items-center gap-2.5 border-b border-warn/30 bg-warn-bg',
          'px-4 py-2.5 text-cell text-warn',
        )}
        >
          <IconRuler size={14} />
          <span>
            <b>Measuring.</b>
            {' Not saved as a map. Only the numbers you add to the property are kept.'}
          </span>
          <span className="ml-auto flex items-center gap-2">
            <Button
              size="toolbar"
              disabled={measure.tape.length === 0}
              onClick={() => setKeeping(true)}
            >
              Keep this drawing as a map…
            </Button>
            <Button variant="primary" size="toolbar" onClick={done}>Done</Button>
          </span>
        </div>
      )}

      <div
        ref={canvasRow}
        className="relative flex flex-1"
        style={{ minHeight: fillHeight }}
      >
        <ToolRail
          tool={tool}
          onPick={pickTool}
          canDraw={canDraw}
          offerMeasure={offerMeasure}
          measuring={measuring}
          canUndo={edit.canUndo}
          onUndo={edit.undo}
          touch={coarse}
        />

        {canvas}

        {/*
          * §5.3: docked at 300px from 1024px up. §7: below that — an iPad in
          * portrait, a narrow laptop window — it is a **slide-over** opened
          * by the map bar's Layers button, over a map that is still tappable
          * behind it. It used to be simply absent there, which is a panel
          * with the layers, the measurements and the history in it that a
          * whole class of screen could not reach at all.
          *
          * The scrim is `bg-transparent`: §7 asks for 0% so the map stays
          * usable while the panel is open, and the panel's own border is
          * what separates them.
          */}
        {slideOver && panelOpen && (
          <button
            type="button"
            aria-label="Close the panel"
            onClick={() => setPanelOpen(false)}
            className="absolute inset-0 z-10 cursor-default bg-transparent"
          />
        )}
        <aside className={cn(
          'w-[300px] shrink-0 flex-col border-l border-line bg-surface',
          slideOver
            ? 'absolute inset-y-0 right-0 z-20 w-[320px] shadow-popover'
            : 'hidden lg:flex',
          slideOver && (panelOpen ? 'flex' : 'hidden'),
        )}
        >
          {/*
            * §5.3's three tabs. A count beside a word only where there is
            * one to give: History is a timeline with no total worth naming,
            * and `History · 0` on a map somebody has just made would be a
            * number that means "not loaded yet".
            */}
          <div className="flex h-11 shrink-0 items-center gap-3.5 border-b border-line px-3.5">
            <PanelTabButton
              label="Layers"
              count={layers.length}
              on={panel === 'layers'}
              onClick={() => setPanel('layers')}
            />
            {offerMeasure && (
              <PanelTabButton
                label="Measurements"
                count={savedParts}
                on={panel === 'measurements'}
                onClick={() => setPanel('measurements')}
              />
            )}
            <PanelTabButton
              label="History"
              on={panel === 'history'}
              onClick={() => setPanel('history')}
            />
            {/*
              * §7: the slide-over's own way out. Tapping the map behind it
              * closes it too, but a panel over a canvas is a panel somebody
              * may well be reading rather than finished with — and tapping
              * the map to dismiss is also a tap on a drawing tool.
              */}
            {slideOver && (
              <button
                type="button"
                aria-label="Close the panel"
                onClick={() => setPanelOpen(false)}
                className={cn(
                  'ml-auto -mr-1.5 flex items-center justify-center rounded-button',
                  'text-text-muted hover:bg-hover',
                  coarse ? 'h-11 w-11' : 'h-8 w-8',
                )}
              >
                <IconClose size={15} />
              </button>
            )}
          </div>

          {panel === 'measurements' && (
            <MeasurementsPanel
              groups={groups}
              tape={measure.tape}
              types={measure.types}
              layerNames={layerNames}
              canEdit={canDraw}
              onSave={() => setSaving(measure.rowsFor())}
              onRename={renamePart}
              onRemove={removePart}
              onRefresh={refreshRow}
              onPick={pickRow}
            />
          )}

          {panel === 'history' && (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {/*
                * The map's own history, its layers' and its shapes' — asked
                * for by map id rather than by a list of record ids, because
                * that list is hundreds long (§4.1).
                */}
              <HistoryTab
                table="property_maps"
                recordId={map.id}
                source={`/property-maps/maps/${map.id}/history`}
                compact
              />
            </div>
          )}

          {panel === 'layers' && (
          <LayersPanel
            layers={layers}
            features={features}
            divisions={divisions}
            hidden={hidden}
            activeId={activeId}
            selectedId={selectedId}
            canEdit={canDraw}
            onToggle={toggle}
            onActivate={setActiveId}
            onSelectShape={(id) => { setTool('select'); setSelectedId(id) }}
            onChangeLayer={(id, patch) => void edit.changeLayer(id, patch)}
            onDeleteLayer={(id, moveTo) => void edit.dropLayer(id, moveTo)}
            onDuplicate={reload}
            onShare={setSharingLayer}
            footer={canDraw
              ? (
                <div className="flex gap-2 px-3 py-2.5">
                  <Button size="toolbar" className="flex-1" onClick={() => setAddingLayer(true)}>
                    <IconPlus size={13} />
                    New layer
                  </Button>
                  <SharingButton onClick={() => setSharingMap(true)} />
                </div>
              )
              : undefined}
          />
          )}
        </aside>
      </div>

      {dialogs}
    </div>
  )
}

/**
 * The drawing bar's live readout — §6.2's `3,980 sq ft · Area · 4 points`.
 *
 * It is the one number somebody is watching while they place corners, so it
 * updates on every Add point rather than only at the end — which is also why
 * it takes the draft rather than the finished shape: there is no finished
 * shape yet.
 */
function readoutOf(draft: Geometry | null, tool: ToolName, points: number): string {
  const counted = `${points} point${points === 1 ? '' : 's'}`
  if (!draft) return `${TOOL_WORDS[tool] ?? 'Drawing'} · ${counted}`

  const { areaSqft, lengthFt } = measuresOf(
    tool === 'select' || tool === 'pan' || tool === 'measure' ? 'area' : tool, draft)
  const size = areaSqft !== null
    ? formatArea(areaSqft)
    : (lengthFt !== null ? formatLength(lengthFt) : null)
  return [size, TOOL_WORDS[tool], counted].filter(Boolean).join(' · ')
}

const TOOL_WORDS: Partial<Record<ToolName, string>> = {
  area: 'Area', rect: 'Rectangle', circle: 'Circle', line: 'Line',
  marker: 'Marker', label: 'Label',
}

/** One of the right panel's three tabs (§5.3). */
function PanelTabButton({ label, count, on, onClick }: {
  label: string
  count?: number
  on: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'whitespace-nowrap border-b-2 py-2.5 text-cell -mb-px',
        on ? 'border-text font-semibold text-text' : 'border-transparent text-text-muted',
      )}
    >
      {label}
      {count !== undefined && ` · ${count}`}
    </button>
  )
}

/**
 * A shape on a layer, as a row the review dialog can write.
 *
 * §5.4's *A shape on a map can be saved too*: the same dialog, the same
 * clash rule, `source = 'map_layer'` and the `featureId` so the part stays
 * linked — which is what lets the panel flag it when the shape is stretched.
 */
function rowsForShape(
  feature: MapFeature, layerName: string, types: MeasurementType[],
): SaveRow[] {
  const { areaSqft, lengthFt } = measuresOf(feature.kind, feature.geometry as Geometry)
  const family = areaSqft === null ? 'length' : 'area'
  const type = types.find((one) => unitFamily(one.unit) === family)
  if (!type) return []
  return [{
    name: feature.name || layerName,
    typeId: type.measurementTypeId,
    typeName: type.name,
    unit: type.unit,
    value: (family === 'area' ? areaSqft : lengthFt) ?? 0,
    source: 'map_layer',
    featureId: feature.id,
  }]
}

/**
 * `Drawing on <layer> ▾` — §5.3's chip.
 *
 * Amber and disabled when the active layer cannot be changed, with the words
 * the spec names, rather than letting somebody draw a shape the save would
 * refuse.
 */
function ActiveLayerChip({ layers, active, canDraw, onPick, save }: {
  layers: MapLayer[]
  active: MapLayer | null
  canDraw: boolean
  onPick: (id: string) => void
  save: SaveState
}) {
  const blocked = canDraw && !active
  return (
    <span className={cn(
      'inline-flex h-[30px] items-center gap-2 rounded-button px-2.5 text-sub shadow-popover',
      blocked ? 'bg-warn-bg text-warn' : 'bg-surface',
    )}
    title={blocked ? 'Pick a layer you can change' : undefined}
    >
      <span className="text-text-muted">Drawing on</span>
      {active
        ? (
          <>
            <span
              aria-hidden
              className="h-3 w-3 shrink-0 rounded-tile-small"
              style={{ background: LAYER_PALETTE[active.color] }}
            />
            <select
              value={active.id}
              onChange={(event) => onPick(event.target.value)}
              aria-label="Drawing on"
              className="cursor-pointer bg-transparent font-bold text-text outline-none"
            >
              {layers.map((one) => (
                <option key={one.id} value={one.id}>{one.name}</option>
              ))}
            </select>
            <IconChevronDown size={12} className="text-text-faint" />
          </>
        )
        : <b>no layer you can change</b>}
      {!isLoud(save) && save.kind !== 'idle' && (
        <>
          <span aria-hidden className="text-text-faint">·</span>
          <StatusPill state={save} inline />
        </>
      )}
    </span>
  )
}

/**
 * What the canvas paints dashed: the shape being drawn, or the one being
 * dragged. Never both, because only one gesture is in hand at a time.
 */
function draftShape(
  drawn: Geometry | null, dragged: Geometry | null,
  drawColour: LayerColor | undefined, dragColour: LayerColor | undefined,
): { geometry: Geometry; color: LayerColor } | null {
  if (dragged && dragColour) return { geometry: dragged, color: dragColour }
  if (drawn && drawColour) return { geometry: drawn, color: drawColour }
  return null
}

/** The popup's own width plus the gap it keeps from the shape. */
const POPUP_W = 310
const POPUP_GAP = 16

/**
 * Beside the shape, in the canvas's own coordinates.
 *
 * **Whichever side has more room**, rather than right-then-flip: a shape wide
 * enough that neither side fits took the flip, hit the left clamp at 8px and
 * landed on top of itself. Picking the roomier side and clamping means the
 * popup covers as little of the shape as the canvas allows.
 */
function popupAt(
  map: MapLibreMap | null, geometry: unknown, box: HTMLElement | null,
): { x: number; y: number } {
  if (!map) return { x: POPUP_GAP, y: 56 }
  const points = pointsIn(geometry)
  if (points.length === 0) return { x: POPUP_GAP, y: 56 }

  const screen = points.map((one) => map.project(one))
  const left = Math.min(...screen.map((one) => one.x))
  const right = Math.max(...screen.map((one) => one.x))
  const top = Math.min(...screen.map((one) => one.y))
  const width = box?.clientWidth ?? 0
  const height = box?.clientHeight ?? 0

  const roomRight = width - (right + POPUP_GAP)
  const roomLeft = left - POPUP_GAP
  const x = roomRight >= POPUP_W || roomRight >= roomLeft
    ? right + POPUP_GAP
    : left - POPUP_GAP - POPUP_W

  return {
    x: Math.max(POPUP_GAP, Math.min(x, Math.max(POPUP_GAP, width - POPUP_W - POPUP_GAP))),
    /* And never below the canvas: a popup half off the bottom is unreachable. */
    y: Math.max(POPUP_GAP, Math.min(top, Math.max(POPUP_GAP, height - 240))),
  }
}

const pointsIn = (geometry: unknown): [number, number][] => {
  const shape = geometry as { type?: string; coordinates?: unknown }
  if (shape?.type === 'Point') return [shape.coordinates as [number, number]]
  if (shape?.type === 'LineString') return shape.coordinates as [number, number][]
  if (shape?.type === 'Polygon') return (shape.coordinates as [number, number][][])[0] ?? []
  return []
}

/**
 * The bar's one sentence about how this map is shared.
 *
 * It describes what is *there* rather than what the default is: a map whose
 * layers have all been narrowed should not say "Shared with All crews by
 * default", which is the line the mockup draws for a map that has not been.
 */
function shareSummary(layers: { audience: string }[]): string {
  const all = layers.filter((layer) => layer.audience === 'all').length
  if (layers.length === 0) return 'No layers yet · nothing is shared with crews'
  if (all === layers.length) {
    return 'Shared with All crews by default · layers can be narrowed by division'
  }
  if (all === 0) return 'Every layer is narrowed · no layer goes to all crews'
  return `${all} of ${layers.length} layers go to all crews · the rest are narrowed`
}

function readHidden(mapId: string): ReadonlySet<string> {
  try {
    const stored = window.localStorage.getItem(HIDDEN(mapId))
    const parsed: unknown = stored ? JSON.parse(stored) : null
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

const readSnap = (): boolean => {
  try { return window.localStorage.getItem(SNAP_KEY) !== 'off' } catch { return true }
}

const writeSnap = (on: boolean): void => {
  try { window.localStorage.setItem(SNAP_KEY, on ? 'on' : 'off') } catch { /* private */ }
}

export type { LayerColor }
